import React, { ReactNode, useEffect, useRef, useState } from 'react';

type LazyVisibleProps = {
  children: ReactNode;
  /** 마운트 전 자리를 채울 내용 — 높이를 유지해 레이아웃 시프트(CLS)를 막는다 */
  placeholder?: ReactNode;
  /** 뷰포트에 이만큼 가까워지면 미리 마운트한다 (스크롤이 닿기 전에 준비) */
  rootMargin?: string;
  className?: string;
};

/**
 * @description
 * 자식을 뷰포트 근처에 올 때까지 마운트하지 않는다.
 *
 * Google Maps 처럼 스크립트가 수백 KB인 위젯이 화면 아래에 있으면서도
 * 초기 로딩 경로를 점유하는 것을 막기 위한 것이다 (매물 상세에서 604KB).
 * 한 번 보이면 계속 마운트 상태를 유지한다 — 스크롤 왕복마다 재로딩되면
 * 오히려 더 느려지고 지도 상태(중심·줌)도 초기화된다.
 *
 * IntersectionObserver 미지원 환경에서는 즉시 마운트해 기능 저하가 없게 한다.
 */
const LazyVisible = ({ children, placeholder = null, rootMargin = '200px', className }: LazyVisibleProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(() => typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    if (isVisible) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(el);

    return () => observer.disconnect();
  }, [isVisible, rootMargin]);

  return (
    <div ref={ref} className={className}>
      {isVisible ? children : placeholder}
    </div>
  );
};

export default LazyVisible;
